enum ComplementStatus {
    None = 0,
    Requested = 1,
    Received = 2,
    Acknowledged = 3,
}

interface Submission {
    readonly id: string;
    readonly eid: number;
    status: "locked" | "auditing" | "passed" | "pending";
    createdAt: string;
    updatedAt: string;
}

interface Audit {
    readonly id: string;
    comment: string;
    createdAt: string;
    updatedAt: string;
}

interface Report {
    readonly id: string;
    readonly eid: number;
    readonly oid: number;
    name: string;
    municipalityCode: string;
    principal: {
        readonly id: number;
        name: string | null;
        readonly personalNumber: string;
        address: string | null;
        city: string | null;
        zipCode: string | null;
    };
    trustee: {
        readonly id: number;
        readonly personalNumber: string;
        name: string | null;
        email: string | null;
        phone: string | null;
        address: string | null;
        city: string | null;
        zipCode: string | null;
    };
    signed: boolean;
    registered: boolean;
    reviewStatus: 0 | 1 | 2 | 3 | 4;
    reviewNotes: string;
    complementStatus: ComplementStatus;
    variant: string;
    attachments: Array<Attachment>;
    sections?: Array<ReportSection>;
    incomings: Array<Category>;
    outgoings: Array<Category>;
    accounts: Array<Finances>;
    assets: Array<Finances>;
    debts: Array<Finances>;
    startDate: string;
    endDate: string;
    createdAt: string;
    updatedAt: string;
    receivedAt: string;
}

type Attachment = {
    md5: string;
    attachmentType: "AnnualReport" | "Statement" | "SignedReport" | "AttachmentComplement";
    mimeType: string;
    name: string;
    size: number;
};

type ReportSection = {
    key: string;
    from: number;
    to: number;
};

type Category = {
    description: string;
    amount: number;
    comment?: string;
    status: 0 | 1 | 2 | 3;
};

type Finances = {
    note: string;
    description: string;
    startAmount: number;
    endAmount: number;
    comment?: string;
    status: 0 | 1 | 2 | 3;
};

type VerificationView = {
    eid: number;
    tid: number;
    amount: string;
    tag_name?: string;
    tax_amount: string | null;
    transaction_date: string;
    transaction_description: string;
    transaction_verified: boolean;
};

type CategoryView = Category & {
    sum_tax_amount: string;
    verifications: VerificationView[];
    has_attachments: boolean;
    id: number;
};

type ReportsList = Array<{
    readonly id: Report["id"];
    readonly eid: Report["eid"];
    readonly sid: Submission["id"];
    readonly oid: Report["oid"];
    principal: {
        name: Report["principal"]["name"];
        readonly personalNumber: Report["principal"]["personalNumber"];
    };
    trustee: Report["trustee"];
    registered: Report["registered"];
    reviewStatus: Report["reviewStatus"];
    variant: Report["variant"];
    startDate: Report["startDate"];
    endDate: Report["endDate"];
    receivedAt: Report["receivedAt"];
    complementStatus?: ComplementStatus;
}>;

type ReportsPagedResponse = {
    items: ReportsList;
    total: number;
    nextCursor: number | null;
    hasMore: boolean;
};

export { ComplementStatus };

export type {
    Submission,
    Audit,
    Report,
    Category,
    Attachment,
    VerificationView,
    CategoryView,
    ReportsList,
    ReportsPagedResponse,
};
